import './TierKpiStrip.css';

import { Users, IndianRupee, TrendingUp, ShieldCheck } from 'lucide-react';
import type { MembershipKpiSnapshot } from '../../api/membership.api';

type Props = {
  data: MembershipKpiSnapshot | null | undefined;
  isLoading?: boolean;
};

const NULL_SYMBOL = '—';

const formatCount = (value: number | null): string => {
  if (value == null) return NULL_SYMBOL;
  return value.toLocaleString('en-IN');
};

const formatPaiseAsRupees = (value: number | null): string => {
  if (value == null) return NULL_SYMBOL;
  const rupees = value / 100;
  if (rupees >= 1_00_000) {
    return `₹${(rupees / 1_00_000).toFixed(1).replace(/\.0$/, '')}L`;
  }
  if (rupees >= 1_000) {
    return `₹${(rupees / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  return `₹${rupees.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const formatRupeesPlain = (value: number | null): string => {
  if (value == null) return NULL_SYMBOL;
  const rupees = Math.round(value / 100);
  return `₹${rupees.toLocaleString('en-IN')}`;
};

const formatPercent = (value: number | null): string => {
  if (value == null) return NULL_SYMBOL;
  return `${(value * 100).toFixed(1).replace(/\.0$/, '')}%`;
};

const TierKpiStrip = ({ data, isLoading }: Props) => {
  const cards = [
    {
      key: 'total-members' as const,
      label: 'Total members',
      value: formatCount(data?.totalMembers ?? null),
      icon: Users,
    },
    {
      key: 'tier-revenue' as const,
      label: 'Tier revenue · 30D',
      value: formatPaiseAsRupees(data?.tierRevenue30dPaise ?? null),
      icon: IndianRupee,
    },
    {
      key: 'avg-member-ltv' as const,
      label: 'Avg member LTV',
      value: formatRupeesPlain(data?.avgMemberLtvPaise ?? null),
      icon: TrendingUp,
    },
    {
      key: 'retention-rate' as const,
      label: 'Retention rate',
      value: formatPercent(data?.retentionRate30d ?? null),
      icon: ShieldCheck,
    },
  ];

  return (
    <div
      className={`tier-kpi-strip${isLoading ? ' tier-kpi-strip--loading' : ''}`}
      role="group"
      aria-label="Membership KPI summary"
      data-testid="tier-kpi-strip"
    >
      {cards.map((card) => {
        const IconCmp = card.icon;
        return (
          <article key={card.key} className="tier-kpi-strip__card" data-testid={`kpi-${card.key}`}>
            <header className="tier-kpi-strip__card-header">
              <span className="tier-kpi-strip__icon" aria-hidden="true">
                <IconCmp size={14} />
              </span>
              <span className="tier-kpi-strip__label">{card.label}</span>
            </header>
            <div className="tier-kpi-strip__value">{card.value}</div>
          </article>
        );
      })}
    </div>
  );
};

export default TierKpiStrip;
